/**
 * TransportTrackingCard
 * Displays transport request tracking information for an order
 * Shows transport status with all stages: Requested → Assigned → Trip Started → Picked Up → In Transit → Delivered
 *
 * @author SAUBHAGYA Development Team
 */

import { TransportTracking } from '@/types/order';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Truck, CheckCircle2, Clock, MapPin, Play, Package, Navigation, Flag } from 'lucide-react';
import { motion } from 'framer-motion';

interface TransportTrackingCardProps {
  transport?: TransportTracking;
}

/**
 * Transport status stages in order
 */
const TRANSPORT_STAGES = [
  {
    id: 'requested',
    label: 'Requested',
    description: 'Transport request created',
    icon: Package,
    statuses: ['NEW', 'OPEN', 'BROADCASTING', 'PENDING'],
  },
  {
    id: 'assigned',
    label: 'Assigned',
    description: 'Driver assigned',
    icon: Truck,
    statuses: ['ASSIGNED', 'ACCEPTED'],
  },
  {
    id: 'trip_started',
    label: 'Trip Started',
    description: 'Driver started trip',
    icon: Play,
    statuses: ['STARTED', 'ARRIVED_PICKUP'],
  },
  {
    id: 'picked_up',
    label: 'Picked Up',
    description: 'Goods picked up',
    icon: MapPin,
    statuses: ['PICKED_UP'],
  },
  {
    id: 'in_transit',
    label: 'In Transit',
    description: 'On the way',
    icon: Navigation,
    statuses: ['IN_TRANSIT', 'ARRIVED_DROP'],
  },
  {
    id: 'delivered',
    label: 'Delivered',
    description: 'Delivery complete',
    icon: Flag,
    statuses: ['DELIVERED', 'COMPLETED'],
  },
];

/**
 * Get the current stage index based on transport status
 */
function getCurrentStageIndex(status?: string): number {
  if (!status) return 0;
  const statusUpper = status.toUpperCase();

  for (let i = TRANSPORT_STAGES.length - 1; i >= 0; i--) {
    if (TRANSPORT_STAGES[i].statuses.includes(statusUpper)) {
      return i;
    }
  }
  return 0; // Default to first stage
}

/**
 * Get badge color based on transport status
 */
function getStatusColor(status?: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (!status) return 'outline';

  const statusUpper = status.toUpperCase();
  if (['NEW', 'OPEN', 'BROADCASTING', 'PENDING'].includes(statusUpper)) return 'secondary';
  if (['ASSIGNED', 'ACCEPTED'].includes(statusUpper)) return 'secondary';
  if (['STARTED', 'ARRIVED_PICKUP', 'PICKED_UP', 'IN_TRANSIT', 'ARRIVED_DROP'].includes(statusUpper)) return 'default';
  if (['DELIVERED', 'COMPLETED'].includes(statusUpper)) return 'default';
  if (statusUpper === 'CANCELLED') return 'destructive';

  return 'outline';
}

/**
 * Format status text for display
 */
function formatStatusText(status?: string): string {
  if (!status) return 'No Transport';
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Format date/time for display
 */
function formatDateTime(dateTime?: string): string {
  if (!dateTime) return '-';
  try {
    const date = new Date(dateTime);
    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  } catch {
    return dateTime;
  }
}

export function TransportTrackingCard({ transport }: TransportTrackingCardProps) {
  // Show different state based on transport info
  const hasTransportInfo = transport?.transportRequestId || transport?.transportRequestNumber || transport?.transportStatus;

  // Don't show card if no transport info at all
  if (!hasTransportInfo) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card className="p-6 border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-slate-300 rounded-lg">
              <Truck className="h-5 w-5 text-slate-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">Transport Tracking</h3>
          </div>
          <p className="text-sm text-slate-600">
            Transport request will be created upon order confirmation
          </p>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card className="p-6 border-slate-200 bg-gradient-to-br from-white to-slate-50">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Truck className="h-5 w-5 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900">Transport Tracking</h3>
        </div>

        {/* Request ID Only */}
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-xs font-semibold text-blue-600 mb-1">REQUEST ID</p>
          <p className="text-sm font-mono text-blue-900">{transport.transportRequestNumber || '-'}</p>
        </div>
      </Card>
    </motion.div>
  );
}
